import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import Analytics from './Analytics';
import './AdminPanel.css';

function AdminPanel({ showToast }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [session, setSession] = useState(localStorage.getItem('adminSession') || '');
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('prompts');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('');

  useEffect(() => {
    if (session) {
      loadPrompts();
    } else {
      setPrompts([]);
      setError(null);
      setSuccess(null);
    }
  }, [session]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (success) {
      setError(null);
    }
  }, [success]);

  useEffect(() => {
    if (!session) {
      const timer = setTimeout(() => setError(null), 100);
      return () => clearTimeout(timer);
    }
  }, [session]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await adminAPI.login(username, password);
      const sessionId = response.data.session;
      
      setUsername('');
      setPassword('');
      setError(null);
      setSession(sessionId);
      localStorage.setItem('adminSession', sessionId);
      setSuccess('Login successful!');
      if (showToast) showToast('Login successful!', 'success');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Login failed';
      setError(errorMsg);
      setSession('');
      localStorage.removeItem('adminSession');
      setSuccess(null);
      if (showToast) showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setSession('');
    localStorage.removeItem('adminSession');
    setPrompts([]);
    setSuccess('Logged out successfully');
    if (showToast) showToast('Logged out successfully', 'info');
  };

  const loadPrompts = React.useCallback(async () => {
    if (!session) return;
    
    try {
      setLoading(true);
      const response = await adminAPI.getAll(session);
      const promptsData = response.data || [];
      // Temporary debug - check if is_deleted field exists
      if (promptsData.length > 0) {
        console.log('First prompt sample:', {
          id: promptsData[0].id,
          title: promptsData[0].title,
          is_deleted: promptsData[0].is_deleted,
          is_deleted_type: typeof promptsData[0].is_deleted,
          all_keys: Object.keys(promptsData[0])
        });
      }
      setPrompts(promptsData);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        if (!loading) {
          setError('Session expired. Please login again.');
        }
        setSession('');
        localStorage.removeItem('adminSession');
        setPrompts([]);
      } else {
        setError(err.response?.data?.error || 'Failed to load prompts');
      }
    } finally {
      setLoading(false);
    }
  }, [session]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) {
      return;
    }

    try {
      await adminAPI.delete(id, session);
      setSuccess('Prompt deleted successfully');
      if (showToast) showToast('Prompt deleted successfully', 'success');
      loadPrompts();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete prompt';
      setError(errorMsg);
      if (showToast) showToast(errorMsg, 'error');
    }
  };

  const handleRestore = async (id) => {
    if (!window.confirm('Are you sure you want to restore this prompt?')) {
      return;
    }

    try {
      await adminAPI.restore(id, session);
      setSuccess('Prompt restored successfully');
      if (showToast) showToast('Prompt restored successfully', 'success');
      loadPrompts();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to restore prompt';
      setError(errorMsg);
      if (showToast) showToast(errorMsg, 'error');
    }
  };

  const handleDeletePermanently = async (id) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this prompt? This action cannot be undone!')) {
      return;
    }

    try {
      await adminAPI.deletePermanently(id, session);
      setSuccess('Prompt permanently deleted');
      if (showToast) showToast('Prompt permanently deleted', 'success');
      loadPrompts();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete prompt';
      setError(errorMsg);
      if (showToast) showToast(errorMsg, 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      showToast('Please select prompts to delete', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} prompt(s)?`)) {
      return;
    }

    try {
      await adminAPI.bulkDelete(selectedIds, session);
      setSuccess(`${selectedIds.length} prompt(s) deleted successfully`);
      if (showToast) showToast(`${selectedIds.length} prompt(s) deleted successfully`, 'success');
      setSelectedIds([]);
      loadPrompts();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete prompts';
      setError(errorMsg);
      if (showToast) showToast(errorMsg, 'error');
    }
  };

  const handleBulkUpdateCategory = async () => {
    if (selectedIds.length === 0) {
      showToast('Please select prompts to update', 'error');
      return;
    }
    if (!bulkCategory.trim()) {
      showToast('Please enter a category', 'error');
      return;
    }

    try {
      await adminAPI.bulkUpdateCategory(selectedIds, bulkCategory, session);
      setSuccess(`Category updated for ${selectedIds.length} prompt(s)`);
      if (showToast) showToast(`Category updated for ${selectedIds.length} prompt(s)`, 'success');
      setSelectedIds([]);
      setBulkCategory('');
      loadPrompts();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to update categories';
      setError(errorMsg);
      if (showToast) showToast(errorMsg, 'error');
    }
  };

  const handleBulkRestore = async () => {
    if (selectedIds.length === 0) {
      showToast('Please select prompts to restore', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to restore ${selectedIds.length} prompt(s)?`)) {
      return;
    }

    try {
      await adminAPI.bulkRestore(selectedIds, session);
      setSuccess(`${selectedIds.length} prompt(s) restored successfully`);
      if (showToast) showToast(`${selectedIds.length} prompt(s) restored successfully`, 'success');
      setSelectedIds([]);
      loadPrompts();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to restore prompts';
      setError(errorMsg);
      if (showToast) showToast(errorMsg, 'error');
    }
  };

  const handleBulkDeletePermanently = async () => {
    if (selectedIds.length === 0) {
      showToast('Please select prompts to delete permanently', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to PERMANENTLY delete ${selectedIds.length} prompt(s)? This action cannot be undone!`)) {
      return;
    }

    try {
      await adminAPI.bulkDeletePermanently(selectedIds, session);
      setSuccess(`${selectedIds.length} prompt(s) permanently deleted`);
      if (showToast) showToast(`${selectedIds.length} prompt(s) permanently deleted`, 'success');
      setSelectedIds([]);
      loadPrompts();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete prompts';
      setError(errorMsg);
      if (showToast) showToast(errorMsg, 'error');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(pid => pid !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === prompts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(prompts.map(p => p.id));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (!session) {
    return (
      <div className="container">
        <h1>Admin Panel</h1>
        {error && !loading && !success && <div className="error">{error}</div>}
        {success && !loading && <div className="success">{success}</div>}
        
        <form onSubmit={handleLogin} className="admin-login-form">
          <div className="form-group">
            <label className="label">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input"
              required
            />
          </div>
          
          <div className="form-group">
            <label className="label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
            />
          </div>
          
          <button type="submit" className="button button-primary" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <button onClick={handleLogout} className="button button-danger">
          Logout
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="admin-tabs">
        <button
          className={`admin-tab-button ${activeTab === 'prompts' ? 'active' : ''}`}
          onClick={() => setActiveTab('prompts')}
        >
          Manage Prompts
        </button>
        <button
          className={`admin-tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
      </div>

      {activeTab === 'prompts' && (
        <>
          {loading && prompts.length === 0 ? (
            <div className="loading">Loading prompts...</div>
          ) : (
            <div className="admin-prompts">
              <div className="admin-header-actions">
                <h2>All Prompts ({prompts.length})</h2>
                {selectedIds.length > 0 && (
                  <div className="bulk-actions">
                    <span className="selected-count">{selectedIds.length} selected</span>
                    <input
                      type="text"
                      placeholder="New category"
                      value={bulkCategory}
                      onChange={(e) => setBulkCategory(e.target.value)}
                      className="input"
                      style={{ width: '150px', marginRight: '10px' }}
                    />
                    <button
                      onClick={handleBulkUpdateCategory}
                      className="button button-primary button-small"
                    >
                      Update Category
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="button button-danger button-small"
                    >
                      Delete Selected
                    </button>
                    <button
                      onClick={handleBulkRestore}
                      className="button button-success button-small"
                    >
                      Restore Selected
                    </button>
                    <button
                      onClick={handleBulkDeletePermanently}
                      className="button button-danger button-small"
                    >
                      Delete Permanently
                    </button>
                    <button
                      onClick={() => setSelectedIds([])}
                      className="button button-small"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div className="admin-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={selectedIds.length === prompts.length && prompts.length > 0}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th>ID</th>
                      <th>Title</th>
                      <th>Username</th>
                      <th>Category</th>
                      <th>Likes</th>
                      <th>Dislikes</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prompts.map(prompt => {
                      // Check for deleted status - handle various formats (1, "1", true, etc.)
                      const isDeleted = prompt.is_deleted === 1 || 
                                       prompt.is_deleted === true || 
                                       prompt.is_deleted === "1" ||
                                       String(prompt.is_deleted) === "1";
                      return (
                        <tr key={prompt.id} className={isDeleted ? 'deleted-row' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(prompt.id)}
                              onChange={() => toggleSelect(prompt.id)}
                            />
                          </td>
                          <td>{prompt.id}</td>
                          <td>
                            {prompt.title}
                            {isDeleted && <span className="deleted-badge">DELETED</span>}
                          </td>
                          <td>{prompt.username}</td>
                          <td>{prompt.category}</td>
                          <td>{prompt.likes || 0}</td>
                          <td>{prompt.dislikes || 0}</td>
                          <td>{formatDate(prompt.createdAt || prompt.created_at)}</td>
                          <td>
                            {isDeleted ? (
                              <>
                                <button
                                  onClick={() => handleRestore(prompt.id)}
                                  className="button button-success button-small"
                                  style={{ marginRight: '5px' }}
                                >
                                  Restore
                                </button>
                                <button
                                  onClick={() => handleDeletePermanently(prompt.id)}
                                  className="button button-danger button-small"
                                >
                                  Delete Permanently
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleDelete(prompt.id)}
                                className="button button-danger button-small"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'analytics' && (
        <Analytics />
      )}
    </div>
  );
}

export default AdminPanel;
